if (!customElements.get('collection-description')) {
  customElements.define(
    'collection-description',
    class extends HTMLElement {
      connectedCallback() {
        this.preview = this.querySelector('.collection-description__preview');
        this.previewText = this.querySelector('.collection-description__preview-text');
        this.ellipsis = this.querySelector('.collection-description__ellipsis');
        this.fullContent = this.querySelector('.collection-description__full');
        this.button = this.querySelector('.collection-description__toggle');
        if (!this.preview || !this.previewText || !this.ellipsis || !this.fullContent || !this.button) return;

        this.expanded = false;
        this.text = this.getPreviewText(this.fullContent);
        // Fit whole graphemes so emoji and accented letters are not split at the cutoff.
        this.characters = typeof Intl.Segmenter === 'function'
          ? Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(this.text), (part) => part.segment)
          : Array.from(this.text);
        this.controller = new AbortController();
        this.button.addEventListener('click', () => this.toggle(), { signal: this.controller.signal });
        this.queueUpdate = () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => {
            if (this.isConnected) this.update();
          });
        };
        window.addEventListener('resize', this.queueUpdate, { signal: this.controller.signal });
        this.observer = new ResizeObserver(this.queueUpdate);
        this.observer.observe(this);
        if (document.fonts) {
          document.fonts.ready.then(this.queueUpdate);
          document.fonts.addEventListener('loadingdone', this.queueUpdate, { signal: this.controller.signal });
        }
        this.update();
      }

      getPreviewText(element) {
        // Add separators so adjacent paragraphs, lists and line breaks do not run together.
        const copy = element.cloneNode(true);
        copy.querySelectorAll('script, style').forEach((node) => node.remove());
        copy.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, br, tr').forEach((node) => {
          node.before(document.createTextNode(' '));
          node.after(document.createTextNode(' '));
        });
        return copy.textContent.replace(/\s+/g, ' ').trim();
      }

      fitExcerpt(maxHeight) {
        // Measure text and the complete "... Read More" suffix in the same inline flow.
        let low = 0;
        let high = this.characters.length;
        while (low < high) {
          const middle = Math.ceil((low + high) / 2);
          this.previewText.textContent = this.characters.slice(0, middle).join('');
          if (this.preview.scrollHeight <= maxHeight) {
            low = middle;
          } else {
            high = middle - 1;
          }
        }

        let excerpt = this.characters.slice(0, low).join('');
        // Prefer a complete final word without discarding a large part of the final line.
        if (/\S$/.test(excerpt) && /^\S/.test(this.characters[low] || '')) {
          const boundary = excerpt.lastIndexOf(' ');
          if (boundary > 0 && excerpt.length - boundary <= 24) excerpt = excerpt.slice(0, boundary);
        }
        this.previewText.textContent = excerpt.replace(/[\s.\u2026]+$/, '');
      }

      update() {
        // A hidden editor section cannot be measured; ResizeObserver retries when it appears.
        if (!this.getBoundingClientRect().width) return;
        this.preview.hidden = false;
        this.previewText.hidden = false;
        this.previewText.textContent = this.text;
        this.fullContent.hidden = false;
        this.ellipsis.hidden = true;
        this.button.hidden = true;
        this.classList.remove('is-expanded');

        const style = window.getComputedStyle(this.preview);
        const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.6;
        const lines = parseInt(window.getComputedStyle(this).getPropertyValue('--description-lines'), 10) || 2;
        const maxHeight = Math.ceil(lineHeight * lines);
        const hasOverflow = this.preview.scrollHeight > maxHeight
          || (Boolean(this.text) && this.fullContent.scrollHeight > maxHeight);

        this.preview.hidden = !hasOverflow;
        this.fullContent.hidden = hasOverflow && !this.expanded;
        this.button.hidden = !hasOverflow;
        this.button.textContent = this.expanded ? this.dataset.readLess : this.dataset.readMore;
        this.button.setAttribute('aria-expanded', String(!this.fullContent.hidden));
        this.classList.toggle('is-expanded', hasOverflow && this.expanded);

        if (hasOverflow && !this.expanded) {
          this.ellipsis.hidden = false;
          this.fitExcerpt(maxHeight);
        } else {
          this.previewText.hidden = true;
        }
      }

      toggle() {
        this.expanded = !this.expanded;
        this.update();
        // Keep the control visible when a long expanded description is collapsed.
        if (!this.expanded) {
          const bounds = this.button.getBoundingClientRect();
          if (bounds.bottom < 0 || bounds.top > window.innerHeight) {
            this.button.scrollIntoView({ block: 'center', behavior: 'auto' });
          }
        }
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        cancelAnimationFrame(this.frame);
      }
    }
  );
}
